import { ChevronLeft, ChevronRight } from 'lucide-react'
import MushafPage from './MushafPage'
import { cn } from '@/utils/cn'
import { useTranslation } from 'react-i18next'

interface MushafSpreadProps {
  currentPage: number
  onPageChange: (page: number) => void
  leftPageContent?: string
  rightPageContent?: string
  highlightedWord?: { ayahNumber: number; wordIndex: number }
  onWordClick?: (ayahNumber: number, wordIndex: number) => void
  className?: string
}

const MushafSpread = ({
  currentPage,
  onPageChange,
  leftPageContent,
  rightPageContent,
  highlightedWord,
  onWordClick,
  className,
}: MushafSpreadProps) => {
  const { t } = useTranslation()
  
  let rightPageNumber = currentPage
  if (rightPageNumber % 2 !== 0) {
    rightPageNumber = currentPage - 1
  }
  if (rightPageNumber < 2) {
    rightPageNumber = 2
  }
  const leftPageNumber = rightPageNumber + 1

  const canGoPrevious = rightPageNumber > 2
  const canGoNext = leftPageNumber < 604

  const handlePrevious = () => {
    if (canGoPrevious) {
      onPageChange(rightPageNumber - 2)
    }
  }

  const handleNext = () => {
    if (canGoNext) {
      onPageChange(rightPageNumber + 2)
    }
  }

  return (
    <div className={cn('relative', className)}>
      <div className="flex gap-4 lg:gap-6 items-start justify-center">
        <button
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          className={cn(
            'hidden md:flex items-center justify-center w-12 h-12 rounded-full transition-all',
            'sticky top-1/2 -translate-y-1/2',
            canGoPrevious
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/50 hover:scale-110'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
          )}
          aria-label={t('common.previous')}
        >
          <ChevronRight className="w-6 h-6" />
        </button>

        <div className="flex-1 max-w-md">
          <MushafPage
            pageNumber={rightPageNumber}
            content={rightPageContent}
            isLeftPage={false}
            highlightedWord={highlightedWord}
            onWordClick={onWordClick}
          />
        </div>

        <div className="hidden md:block flex-1 max-w-md">
          <MushafPage
            pageNumber={leftPageNumber}
            content={leftPageContent}
            isLeftPage={true}
            highlightedWord={highlightedWord}
            onWordClick={onWordClick}
          />
        </div>

        <button
          onClick={handleNext}
          disabled={!canGoNext}
          className={cn(
            'hidden md:flex items-center justify-center w-12 h-12 rounded-full transition-all',
            'sticky top-1/2 -translate-y-1/2',
            canGoNext
              ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-900/50 hover:scale-110'
              : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-50'
          )}
          aria-label={t('common.next')}
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
      </div>

      <div className="md:hidden flex justify-between items-center mt-4 px-4">
        <button
          onClick={handlePrevious}
          disabled={!canGoPrevious}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
            canGoPrevious
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
          )}
        >
          <ChevronRight className="w-5 h-5" />
          <span>{t('common.previous')}</span>
        </button>
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {t('quran.page')} {currentPage} {t('quran.of')} 604
        </div>
        <button
          onClick={handleNext}
          disabled={!canGoNext}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors',
            canGoNext
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
          )}
        >
          <span>{t('common.next')}</span>
          <ChevronLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
        <p>
          {t('quran.pages')} {rightPageNumber} - {leftPageNumber}
        </p>
      </div>
    </div>
  )
}

export default MushafSpread;